import { ChallengeService, TaskService } from '@duck/api-definitions'
import { TranslationKey } from '@/modules'
import {
  SparklesIcon,
  BoltIcon,
  ClockIcon,
  FireIcon
} from '@heroicons/react/24/outline'
import { ComponentType, SVGProps } from 'react'

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>

/**
 * Maps ChallengeService.TimeCategoryEnum to icons
 */
export const timeCategoryIconMap: Record<ChallengeService.TimeCategoryEnum, HeroIcon> = {
  [ChallengeService.TimeCategoryEnum.New]: SparklesIcon,
  [ChallengeService.TimeCategoryEnum.UpTo30]: BoltIcon,
  [ChallengeService.TimeCategoryEnum.UpTo60]: ClockIcon,
  [ChallengeService.TimeCategoryEnum._60OrMore]: FireIcon
}

/**
 * Maps ChallengeService.TimeCategoryEnum to translation keys
 */
export const timeCategoryDisplayMap: Record<ChallengeService.TimeCategoryEnum, TranslationKey> = {
  [ChallengeService.TimeCategoryEnum.New]: 'challenges.timeCategoryNew',
  [ChallengeService.TimeCategoryEnum.UpTo30]: 'challenges.timeCategoryUpTo30',
  [ChallengeService.TimeCategoryEnum.UpTo60]: 'challenges.timeCategoryUpTo60',
  [ChallengeService.TimeCategoryEnum._60OrMore]: 'challenges.timeCategory60OrMore'
}

/**
 * The order of time categories for display
 */
export const timeCategoryOrder: ChallengeService.TimeCategoryEnum[] = [
  ChallengeService.TimeCategoryEnum.New,
  ChallengeService.TimeCategoryEnum.UpTo30,
  ChallengeService.TimeCategoryEnum.UpTo60,
  ChallengeService.TimeCategoryEnum._60OrMore
]

/**
 * Maps TaskService.TaskDifficultyEnum to translation keys
 */
export const taskDifficultyDisplayMap: Record<TaskService.TaskDifficultyEnum, TranslationKey> = {
  [TaskService.TaskDifficultyEnum.Easy]: 'tasks.difficulty.easy',
  [TaskService.TaskDifficultyEnum.Intermediate]: 'tasks.difficulty.intermediate',
  [TaskService.TaskDifficultyEnum.Hard]: 'tasks.difficulty.hard',
  [TaskService.TaskDifficultyEnum.Expert]: 'tasks.difficulty.expert'
}

/**
 * Maps ChallengeService.ChallengeDifficultyEnum to translation keys
 */
export const challengeDifficultyDisplayMap: Record<ChallengeService.ChallengeDifficultyEnum, TranslationKey> = {
  [ChallengeService.ChallengeDifficultyEnum.Easy]: 'tasks.difficulty.easy',
  [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 'tasks.difficulty.intermediate',
  [ChallengeService.ChallengeDifficultyEnum.Hard]: 'tasks.difficulty.hard',
  [ChallengeService.ChallengeDifficultyEnum.Expert]: 'tasks.difficulty.expert'
}

/**
 * Returns Tailwind CSS classes for TaskService difficulty badges
 */
export const getTaskDifficultyColor = (difficulty: TaskService.TaskDifficultyEnum): string => {
  const colorMap = {
    [TaskService.TaskDifficultyEnum.Easy]: 'bg-success/10 text-success border-success/20',
    [TaskService.TaskDifficultyEnum.Intermediate]: 'bg-info/10 text-muted-foreground border-info/20',
    [TaskService.TaskDifficultyEnum.Hard]: 'bg-warning/10 text-warning border-warning/20',
    [TaskService.TaskDifficultyEnum.Expert]: 'bg-error/10 text-error border-error/20'
  }
  return colorMap[difficulty] || 'bg-muted/10 text-muted border-muted/20'
}

/**
 * Returns Tailwind CSS classes for ChallengeService difficulty badges
 */
export const getChallengeDifficultyColor = (difficulty: ChallengeService.ChallengeDifficultyEnum): string => {
  const colorMap = {
    [ChallengeService.ChallengeDifficultyEnum.Easy]: 'bg-success/10 text-success border-success/20',
    [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 'bg-info/10 text-muted-foreground border-info/20',
    [ChallengeService.ChallengeDifficultyEnum.Hard]: 'bg-warning/10 text-warning border-warning/20',
    [ChallengeService.ChallengeDifficultyEnum.Expert]: 'bg-error/10 text-error border-error/20'
  }
  return colorMap[difficulty] || 'bg-muted/10 text-muted border-muted/20'
}

/**
 * Returns Tailwind CSS classes for ChallengeService difficulty badges with accent variant
 * Used in CompletedChallengesPage where Intermediate uses accent color instead of info
 */
export const getChallengeDifficultyColorAccentVariant = (difficulty: ChallengeService.ChallengeDifficultyEnum): string => {
  const colorMap = {
    [ChallengeService.ChallengeDifficultyEnum.Easy]: 'bg-success/10 text-success border-success/20',
    [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 'bg-accent/10 text-accent border-accent/20',
    [ChallengeService.ChallengeDifficultyEnum.Hard]: 'bg-warning/10 text-warning border-warning/20',
    [ChallengeService.ChallengeDifficultyEnum.Expert]: 'bg-error/10 text-error border-error/20'
  }
  return colorMap[difficulty] || 'bg-muted/10 text-muted border-muted/20'
}

